import dayjs from "dayjs";
import { RevisionFilterState, TemplateRevision } from "./types";

export const DATE_VALUE_FORMAT = "YYYY-MM-DD";

export interface RevisionDateRange {
  startDate: string;
  endDate: string;
}

/**
 * Custom range defaults to the last month, i.e. one month back through today.
 * Applied both on first render and whenever the user switches into the
 * "Custom range" chip.
 */
export const defaultCustomRange = (): {
  customFrom: string;
  customTo: string;
} => ({
  customFrom: dayjs().subtract(1, "month").format(DATE_VALUE_FORMAT),
  customTo: dayjs().format(DATE_VALUE_FORMAT),
});

/**
 * The start_date / end_date sent to the templates endpoint for each of the
 * five revision tabs. Date filtering happens server side; only the editor
 * filter below is applied in the browser.
 */
export const rangeToDates = (
  filters: RevisionFilterState,
): RevisionDateRange => {
  const today = dayjs();

  switch (filters.range) {
    case "today":
      return {
        startDate: today.format(DATE_VALUE_FORMAT),
        endDate: today.format(DATE_VALUE_FORMAT),
      };
    case "yesterday": {
      const yesterday = today.subtract(1, "day").format(DATE_VALUE_FORMAT);
      return { startDate: yesterday, endDate: yesterday };
    }
    case "7d":
      return {
        // Inclusive of today, so six days back gives a seven-day window.
        startDate: today.subtract(6, "day").format(DATE_VALUE_FORMAT),
        endDate: today.format(DATE_VALUE_FORMAT),
      };
    case "30d":
      return {
        startDate: today.subtract(29, "day").format(DATE_VALUE_FORMAT),
        endDate: today.format(DATE_VALUE_FORMAT),
      };
    case "custom":
    default: {
      const fallback = defaultCustomRange();
      return {
        startDate: filters.customFrom || fallback.customFrom,
        endDate: filters.customTo || fallback.customTo,
      };
    }
  }
};

/**
 * Only the "Modified by" filter runs in the browser — the date range is
 * applied by the endpoint. Newest first, matching the
 * "Sorted by most recently modified" label.
 */
export const filterRevisions = (
  revisions: TemplateRevision[],
  filters: RevisionFilterState,
): TemplateRevision[] =>
  revisions
    .filter(
      (revision) =>
        !filters.modifiedBy || revision.modifiedBy === filters.modifiedBy,
    )
    .sort(
      (a, b) =>
        new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime(),
    );
